import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Gift, ShoppingCart, Loader2, CheckCircle2, XCircle, Package, CreditCard, Banknote, ArrowLeft, MapPin } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function RewardsStore() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("session");
  const uid = searchParams.get("uid");

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);

  // Checkout form - includes district field
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    district: "",
    state: "",
    pincode: ""
  });
  const [recipientEmail, setRecipientEmail] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid session. No token provided.");
      setLoading(false);
      return;
    }
    initializeStore();
  }, [token]);

  const initializeStore = async () => {
    try {
      const [sessionRes, catalogRes] = await Promise.all([
        axios.post(`${API}/rewards/session/verify-token?token=${token}`),
        axios.get(`${API}/rewards/catalog`)
      ]);
      
      let sessionData = sessionRes.data;
      
      // If we have UID, fetch fresh points and address from Taj API
      if (uid) {
        try {
          const tajRes = await axios.post(`${API}/taj/user-by-uid`, { uid });
          if (tajRes.data.found) {
            // Update session with fresh points
            if (tajRes.data.available_points !== undefined) {
              sessionData = {
                ...sessionData,
                available_points: tajRes.data.available_points,
                fresh_points: true
              };
            }
            // Auto-fill delivery address from API
            if (tajRes.data.address) {
              const addr = tajRes.data.address;
              setAddress({
                line1: addr.address_line1 || "",
                line2: addr.address_line2 || "",
                city: addr.city || "",
                district: addr.district || "",
                state: addr.state || "",
                pincode: addr.pincode || ""
              });
            }
          }
        } catch (tajErr) {
          console.log("Could not fetch fresh data from Taj API, using session data");
        }
      }
      
      setSession(sessionData);
      setCatalog(catalogRes.data);
    } catch (err) {
      let errorMsg = "Session verification failed";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || d.message || "Validation error").join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || detail.message || "Validation error";
        }
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getCategories = () => {
    const categories = new Set(catalog.map(item => item.category));
    return ["all", ...Array.from(categories)];
  };

  const getFilteredCatalog = () => {
    if (selectedCategory === "all") return catalog;
    return catalog.filter(item => item.category === selectedCategory);
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setCheckoutOpen(true);
  };

  const handleCheckout = async () => {
    if (!selectedItem) return;

    // Validate address for physical items
    if (selectedItem.item_type === "physical") {
      if (!address.line1 || !address.city || !address.state || !address.pincode) {
        toast.error("Please fill in complete delivery address");
        return;
      }
    }

    // Validate email for vouchers
    if (selectedItem.item_type === "voucher" && !recipientEmail) {
      toast.error("Please enter email for voucher delivery");
      return;
    }

    setCheckoutLoading(true);
    try {
      const payload = {
        rewards_session_id: session.session_id,
        redemption_type: selectedItem.item_type,
        item_id: selectedItem.id,
        points_to_redeem: selectedItem.points_required,
        delivery_address: selectedItem.item_type === "physical" ? {
          ...address,
          district: address.district
        } : null,
        recipient_details: selectedItem.item_type === "voucher" ? { email: recipientEmail } : null
      };

      const response = await axios.post(`${API}/rewards/checkout`, payload);
      
      // Also submit redemption to Taj Cement if UID is available
      if (uid) {
        try {
          await axios.post(`${API}/taj/redeem`, {
            uid: uid,
            points_requested: selectedItem.points_required
          });
          console.log("Redemption submitted to Taj Cement");
        } catch (tajError) {
          console.error("Failed to submit to Taj Cement:", tajError);
        }
      }
      
      setOrderComplete(response.data);
      toast.success("Redemption successful!");
      
      // Update session points
      setSession(prev => ({
        ...prev,
        available_points: prev.available_points - selectedItem.points_required
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const resetCheckout = () => {
    setCheckoutOpen(false);
    setSelectedItem(null);
    setOrderComplete(null);
    setRecipientEmail("");
    // Don't reset address - keep the auto-filled data for next checkout
  };

  const getAvailablePoints = () => {
    return (session?.available_points || 0) - (session?.reserved_points || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading rewards store...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const backUid = uid || session?.taj_user_id;
    const backUrl = backUid ? `/?uid=${backUid}` : "/";
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full rounded-sm" data-testid="rewards-error-card">
          <CardContent className="pt-6 text-center space-y-4">
            <XCircle className="w-16 h-16 text-destructive mx-auto" />
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Session Error</h2>
            <p className="text-muted-foreground">{typeof error === 'string' ? error : 'Session expired or invalid'}</p>
            <Button 
              variant="outline" 
              className="rounded-none"
              onClick={() => navigate(backUrl)}
              data-testid="rewards-back-home-btn"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Points Header */}
      <header className="points-header sticky top-0 z-40">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
                alt="Taj Cement" 
                className="h-8 w-auto brightness-0 invert"
              />
              <div>
                <p className="text-xs text-primary-foreground/70 uppercase tracking-wider">Available Points</p>
                <p className="points-value" data-testid="rewards-points-balance">
                  {getAvailablePoints().toLocaleString()}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-primary-foreground"
              onClick={() => {
                const backUid = uid || session?.taj_user_id;
                navigate(backUid ? `/?uid=${backUid}` : "/");
              }}
              data-testid="rewards-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Category Filter */}
      <div className="bg-background border-b sticky top-[76px] z-30 py-3 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="category-filter">
            {getCategories().map((category) => (
              <button
                key={category}
                className={`category-chip ${selectedCategory === category ? "active" : ""}`}
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-${category}`}
              >
                {category === "all" ? "All Items" : category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Redemption Options Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-12 rounded-none">
            <TabsTrigger value="all" className="rounded-none" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="physical" className="rounded-none" data-testid="tab-physical">
              <Package className="w-4 h-4 mr-2" />
              Physical
            </TabsTrigger>
            <TabsTrigger value="voucher" className="rounded-none" data-testid="tab-voucher">
              <CreditCard className="w-4 h-4 mr-2" />
              E-Vouchers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="catalog-grid">
              {getFilteredCatalog().map((item) => (
                <ProductCard 
                  key={item.id} 
                  item={item} 
                  availablePoints={getAvailablePoints()}
                  onSelect={handleSelectItem}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="physical" className="mt-6">
            <div className="catalog-grid">
              {getFilteredCatalog().filter(i => i.item_type === "physical").map((item) => (
                <ProductCard 
                  key={item.id} 
                  item={item} 
                  availablePoints={getAvailablePoints()}
                  onSelect={handleSelectItem}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="voucher" className="mt-6">
            <div className="catalog-grid">
              {getFilteredCatalog().filter(i => i.item_type === "voucher").map((item) => (
                <ProductCard 
                  key={item.id} 
                  item={item} 
                  availablePoints={getAvailablePoints()}
                  onSelect={handleSelectItem}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* UPI Transfer - Disabled */}
        <Card className="mt-8 rounded-sm relative overflow-hidden" data-testid="upi-disabled-card">
          <div className="disabled-overlay">
            <div className="disabled-badge">Coming Soon</div>
          </div>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 opacity-50">
              <div className="p-4 bg-muted rounded-sm">
                <Banknote className="w-8 h-8" />
              </div>
              <div>
                <h3 className="font-semibold text-lg" style={{ fontFamily: 'Manrope' }}>UPI Transfer</h3>
                <p className="text-sm text-muted-foreground">Transfer points directly to your bank account</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={(open) => !checkoutLoading && setCheckoutOpen(open)}>
        <DialogContent className="sm:max-w-lg rounded-sm" data-testid="checkout-dialog">
          {orderComplete ? (
            // Success State
            <div className="py-6 text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F2B721 0%, #F9D96A 50%, #F2B721 100%)' }}>
                <CheckCircle2 className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Order Placed!</h2>
                <p className="text-muted-foreground mt-1">Your redemption was successful</p>
              </div>
              
              <div className="bg-muted p-4 rounded-sm text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Order ID</span>
                  <span className="font-mono font-medium" data-testid="order-id">{orderComplete.order_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Item</span>
                  <span className="font-medium">{orderComplete.order_summary?.item_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Points Debited</span>
                  <span className="font-bold" style={{ color: '#F2B721' }}>
                    {orderComplete.points_debited?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className="font-medium">{orderComplete.order_summary?.estimated_delivery}</span>
                </div>
              </div>

              <Button 
                className="w-full h-12 rounded-none uppercase tracking-wider"
                onClick={resetCheckout}
                data-testid="checkout-done-btn"
              >
                Continue Shopping
              </Button>
            </div>
          ) : selectedItem && (
            // Checkout Form
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <ShoppingCart className="w-5 h-5" />
                  Checkout
                </DialogTitle>
                <DialogDescription>
                  Complete your redemption
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
                {/* Item Summary */}
                <div className="flex gap-4 p-4 bg-muted rounded-sm">
                  <img 
                    src={selectedItem.image_url} 
                    alt={selectedItem.name}
                    className="w-20 h-20 object-cover rounded-sm"
                  />
                  <div className="flex-1">
                    <h3 className="font-medium" data-testid="checkout-item-name">{selectedItem.name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedItem.category}</p>
                    <p className="text-lg font-bold mt-1" style={{ color: '#F2B721' }} data-testid="checkout-item-points">
                      {selectedItem.points_required.toLocaleString()} pts
                    </p>
                  </div>
                </div>

                {/* Address Form for Physical Items */}
                {selectedItem.item_type === "physical" && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <MapPin className="w-4 h-4" />
                      Delivery Address
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2">
                      Address has been auto-filled from your profile. You can edit if needed.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Address Line 1 *</Label>
                        <Input
                          className="input-industrial"
                          placeholder="Address Line 1"
                          value={address.line1}
                          onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                          data-testid="checkout-address-line1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Address Line 2</Label>
                        <Input
                          className="input-industrial"
                          placeholder="Address Line 2 (optional)"
                          value={address.line2}
                          onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                          data-testid="checkout-address-line2"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">City *</Label>
                          <Input
                            className="input-industrial"
                            placeholder="City"
                            value={address.city}
                            onChange={(e) => setAddress({ ...address, city: e.target.value })}
                            data-testid="checkout-address-city"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">District</Label>
                          <Input
                            className="input-industrial"
                            placeholder="District"
                            value={address.district}
                            onChange={(e) => setAddress({ ...address, district: e.target.value })}
                            data-testid="checkout-address-district"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">State *</Label>
                          <Input
                            className="input-industrial"
                            placeholder="State"
                            value={address.state}
                            onChange={(e) => setAddress({ ...address, state: e.target.value })}
                            data-testid="checkout-address-state"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">PIN Code *</Label>
                          <Input
                            className="input-industrial"
                            placeholder="PIN Code"
                            value={address.pincode}
                            onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                            maxLength={6}
                            data-testid="checkout-address-pincode"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Email for Vouchers */}
                {selectedItem.item_type === "voucher" && (
                  <div className="space-y-4">
                    <Label className="form-label">Email for Voucher Delivery</Label>
                    <Input
                      className="input-industrial"
                      type="email"
                      placeholder="your@email.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      data-testid="checkout-email"
                    />
                  </div>
                )}

                {/* Order Summary */}
                <div className="order-summary">
                  <div className="order-summary-row">
                    <span>Item Points</span>
                    <span>{selectedItem.points_required.toLocaleString()} pts</span>
                  </div>
                  {selectedItem.mrp > 0 && (
                    <div className="order-summary-row">
                      <span>Eligibility Requirement (2 × MRP ₹{parseInt(selectedItem.mrp).toLocaleString()})</span>
                      <span>{(parseInt(selectedItem.mrp) * 2).toLocaleString()} pts</span>
                    </div>
                  )}
                  <div className="order-summary-row">
                    <span>Available Balance</span>
                    <span>{getAvailablePoints().toLocaleString()} pts</span>
                  </div>
                  <div className="order-summary-total">
                    <span>Points to Redeem</span>
                    <span style={{ color: '#F2B721' }}>{selectedItem.points_required.toLocaleString()} pts</span>
                  </div>
                </div>

                {(() => {
                  const mrpVal = selectedItem.mrp ? parseInt(selectedItem.mrp) : 0;
                  const threshold = Math.max(mrpVal * 2, selectedItem.points_required || 0);
                  if (getAvailablePoints() < threshold) {
                    const reason = mrpVal > 0 && mrpVal * 2 > selectedItem.points_required
                      ? `You need at least ${(mrpVal * 2).toLocaleString()} points (2 × MRP ₹${mrpVal.toLocaleString()}) to redeem this product.`
                      : "Insufficient points.";
                    return (
                      <div className="error-message" data-testid="insufficient-points-msg">
                        {reason} You are short by {(threshold - getAvailablePoints()).toLocaleString()} points.
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setCheckoutOpen(false)}
                  className="rounded-none"
                  disabled={checkoutLoading}
                  data-testid="checkout-cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  className="rounded-none btn-gold"
                  onClick={handleCheckout}
                  disabled={(() => {
                    const mrpVal = selectedItem.mrp ? parseInt(selectedItem.mrp) : 0;
                    const threshold = Math.max(mrpVal * 2, selectedItem.points_required || 0);
                    return checkoutLoading || getAvailablePoints() < threshold;
                  })()}
                  data-testid="checkout-confirm-btn"
                >
                  {checkoutLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                  )}
                  Confirm Redemption
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Product Card Component
function ProductCard({ item, availablePoints, onSelect }) {
  const mrp = item.mrp ? parseInt(item.mrp) : 0;
  const eligibilityThreshold = mrp > 0 ? mrp * 2 : item.points_required;
  const effectiveRequirement = Math.max(eligibilityThreshold, item.points_required || 0);
  const canRedeem = availablePoints >= effectiveRequirement;
  const shortBy = effectiveRequirement - availablePoints;

  return (
    <div 
      className={`product-card ${!canRedeem ? "opacity-60" : ""}`}
      onClick={() => canRedeem && onSelect(item)}
      data-testid={`product-card-${item.id}`}
    >
      <div className="product-card-image">
        <img 
          src={item.image_url} 
          alt={item.name}
          loading="lazy"
        />
        {!canRedeem && (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
            <span className="text-xs font-medium bg-muted px-2 py-1 text-center" data-testid={`not-eligible-${item.id}`}>
              Need {shortBy.toLocaleString()} more pts
            </span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          {item.item_type === "voucher" ? (
            <CreditCard className="w-5 h-5 text-primary bg-background p-0.5 rounded" />
          ) : (
            <Package className="w-5 h-5 text-primary bg-background p-0.5 rounded" />
          )}
        </div>
      </div>
      <div className="product-card-content">
        <p className="product-card-title">{item.name}</p>
        <p className="text-xs text-muted-foreground">{item.provider}</p>
        <p className="product-card-points">{item.points_required.toLocaleString()} pts</p>
        {mrp > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Eligibility: need ≥ {(mrp * 2).toLocaleString()} pts (2 × MRP ₹{mrp.toLocaleString()})
          </p>
        )}
      </div>
    </div>
  );
}
